import React, {Dispatch, SetStateAction, useState} from 'react';
import {gql} from '@apollo/client';
import {Root as DialogRoot} from '@radix-ui/react-dialog';

import {
  ProductVariant,
  SubscriptionItem,
  UpdateSubscriptionInput,
  useGetSimulatedDiscounts,
  useUpdateSubscriptionMutation,
} from '#apollo/schema';
import {Button, Modal, Text, UncertainProgress} from '#component/base';
import {ModalClose} from '#component/base/Modal';
import {PromoCodeAction} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManager.types';
import {getMembershipVariantId} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManager.util';
import {useSubscriptionManager} from '#component/page/Profile/components/ManagePlanTab/SubscriptionManager/SubscriptionManagerContext';
import {managePlanQuery} from '#component/page/Profile/components/ManagePlanTab/useManagePlanTab';
import {PauseSubscriptionQuery} from '#component/page/Profile/components/SubscriptionTab/CTASubscription/SkipNextOrderModal/usePauseSubscriptionItems';
import {profileUpcomingOrderQuery} from '#component/page/Profile/components/SubscriptionTab/useSubscriptionTab';
import {EventType, useAnalytics, UserActionEvent} from '#component/provider/AnalyticsProvider';
import {csEmail} from '#constant/information';
import {useToastQueue} from '#hook';
import {text} from '#theme/text';
import {formattedDate} from '#util';
import {getSubscriptionVariantIdsByVariantIds, SubscriptionStatusEnum} from '#util/subscriptions';

import {DiscountErrorMessageBox} from './DiscountErrorMessageBox';
import DiscountSection from './DiscountSection/DiscountSection';
import {PricedProductVariantRow} from './PricedProductVariantRow';

import {ProductVariantConfirmation} from './UpdateConfirmationModal.types';

import * as css from './UpdateConfirmationModal.css';

export enum DiscountCategory {
  CODE = 'CODE',
  OTHER = 'OTHER',
  SUBSCRIPTION = 'SUBSCRIPTION',
  MEMBERSHIP = 'MEMBERSHIP',
}

export interface Discount {
  type: string;
  variantId?: string;
  value: number;
  category: DiscountCategory;
  // This isn’t working yet as we’re waiting for a backend update
  title: string;
}

export interface DiscountError {
  discount: string;
  code: string;
  message: string;
}

export const getSimulatedDiscounts = gql`
  query GetSimulatedDiscounts($input: SimulatedDiscountsInput!) {
    getSimulatedDiscounts(input: $input) {
      discounts {
        type
        variantId
        value
        category
        title
      }
      discountErrors {
        discount
        code
        message
      }
    }
  }
`;

export const updateSubscriptionProductVariantsMutation = gql`
  mutation UpdateSubscription($subscriptionChanges: UpdateSubscriptionInput!) {
    updateSubscription(subscriptionChanges: $subscriptionChanges) {
      id
    }
  }
`;

export interface UpdateConfirmationModalProps {
  open: boolean;
  onOpenChange: Dispatch<SetStateAction<boolean>>;
  nextBillingDateAt: string;
  addProductVariants: string[];
  removeProductVariants: string[];
  productVariants: ProductVariantConfirmation[];
  subscriptionId: number;
  subscriptionStatus: string;
  allProductVariants: ProductVariant[];
  discountsToSimulate: string[];
  subscriptionItems: SubscriptionItem[];
}

export function UpdateConfirmationModal({
  open,
  onOpenChange,
  nextBillingDateAt,
  addProductVariants,
  removeProductVariants,
  productVariants,
  subscriptionId,
  subscriptionStatus,
  allProductVariants,
  discountsToSimulate,
  subscriptionItems,
}: UpdateConfirmationModalProps) {
  const {track} = useAnalytics();
  const {
    pendingChanges,
    updatePromoCodes,
    hasAutoAppliedPromoCodes,
    setHasAutoAppliedPromoCodes,
    alreadyAppliedPromoCodeError,
    setAlreadyAppliedPromoCodeError,
    setHasAutoAppliedPromoCodesError,
  } = useSubscriptionManager();
  const [updateSubscriptionProductVariants, {loading}] = useUpdateSubscriptionMutation({
    refetchQueries: [
      {query: managePlanQuery},
      {query: PauseSubscriptionQuery},
      {query: profileUpcomingOrderQuery},
    ],
  });
  const [hasUpdateError, setHasUpdateError] = useState(false);

  // removeProductVariants is a list of bottle variants, convert this list to the variants in the subscription
  const removeSubscriptionVariantIds = getSubscriptionVariantIdsByVariantIds(
    subscriptionItems,
    allProductVariants,
    removeProductVariants,
  );

  let hasMembership = false;
  const pvIds: string[] = productVariants.filter(p => p.price !== 0).map(p => p.variantId);
  const {data, loading: queryLoading} = useGetSimulatedDiscounts({
    variables: {
      input: {
        subscriptionId: subscriptionId,
        variantIds: pvIds,
        discountCodes: discountsToSimulate,
      },
    },
  });
  const discounts: Discount[] = (data?.getSimulatedDiscounts?.discounts as Discount[]) || [];

  const discountErrors: DiscountError[] =
    (data?.getSimulatedDiscounts?.discountErrors as DiscountError[]) || [];

  if (discountErrors.length > 0 && hasAutoAppliedPromoCodes) {
    setHasAutoAppliedPromoCodesError(true);
  }
  const {addToast} = useToastQueue();

  let totalDiscounts = 0;
  let totalPriceWithoutDiscounts = 0;
  let totalAccountLevelDiscounts = 0;
  let highestPriceSupplementId: string | undefined;

  // In the two functions below, the user's membership status and most expensive supplement are identified.
  // The most expensive supplement is the one where the supplement's discount value is the same as the
  // supplement's price
  // They are used to determine if a supplement should be shown by itself or as an add on to the Membership.
  // The map function also finds each product's discount based on product variant id
  // and attaches that value to the Product object on the discount property.
  const memberVariantId = getMembershipVariantId(allProductVariants);

  const updatedProducts = productVariants.map(product => {
    const discountsToApply = discounts.filter(d => d.variantId === product.id);
    const productTotalDiscount = discountsToApply.reduce(
      (total, discount) => total + discount.value,
      0,
    );
    if (productTotalDiscount > 0) {
      totalDiscounts += productTotalDiscount;
    }
    totalPriceWithoutDiscounts += product.price;
    if (product.id === memberVariantId) {
      hasMembership = true;
    } else if (product.price == productTotalDiscount) {
      highestPriceSupplementId = product.id;
    }

    product.display = true;

    return {
      ...product,
      discount: productTotalDiscount,
    };
  });

  // Applying account level discounts that are not tied to a product variant
  discounts.forEach(d => {
    if (!d.variantId) {
      totalAccountLevelDiscounts += d.value;
    }
  });

  // If the user has a membership:
  // 1. Set the most expensive supplement's name as the plusSupplement property on
  // the membership PV object (to display together as "Membership with Vitality")
  // 2. Move the membership PV object to the front of the array so that it is shown first in the UI
  // 3. Set the display property on the highest priced supplement PV object to false so that
  // it doesn't get rendered as its own product row
  if (hasMembership) {
    const membershipProductIndex = updatedProducts.findIndex(p => p.id === memberVariantId);
    let highestPriceSupplementTid: string | undefined;
    let membership;

    if (membershipProductIndex !== -1) {
      if (highestPriceSupplementId) {
        const highestPriceSupplement = updatedProducts.find(p => p.id === highestPriceSupplementId);
        highestPriceSupplementTid = highestPriceSupplement?.tid;
        updatedProducts[membershipProductIndex].plusSupplement = highestPriceSupplement?.title;
      }
      membership = updatedProducts.splice(membershipProductIndex, 1)[0];
      updatedProducts.unshift(membership);
    }
    if (highestPriceSupplementId) {
      const supplementIndex = updatedProducts.findIndex(p => p.id === highestPriceSupplementId);
      if (supplementIndex !== -1) {
        updatedProducts[supplementIndex].display = false;
      }
    }

    if (highestPriceSupplementTid && membership) {
      membership.imageURL = `/image/profile/manage-plan/membership-manage-plan.png`;
    }
  }

  const grandTotal = totalPriceWithoutDiscounts - totalDiscounts - totalAccountLevelDiscounts;

  const handleConfirm = async () => {
    try {
      const pausedSubscriptionStatus = subscriptionStatus === SubscriptionStatusEnum.PAUSED;
      const subscriptionChanges: UpdateSubscriptionInput = {
        addProductVariantIds: addProductVariants,
        removeProductVariantIds: removeSubscriptionVariantIds,
      };

      if (pendingChanges?.addDiscountCodes && pendingChanges.addDiscountCodes.length > 0) {
        subscriptionChanges.addDiscountCodes = pendingChanges.addDiscountCodes;
      }

      if (pausedSubscriptionStatus) {
        subscriptionChanges.updateStatusTo = 'ACTIVE';
      }

      await updateSubscriptionProductVariants({
        variables: {subscriptionChanges},
      });

      onOpenChange(false);
      updatePromoCodes('', PromoCodeAction.CLEAR);
      addToast({children: 'Subscription updated!'});
      track<UserActionEvent>({
        evtName: EventType.UserAction,
        actionName: 'Manage My Plan - Confirm Changes',
      });
    } catch {
      setHasUpdateError(true);
    }
  };

  const handleReturnToManagePlan = () => {
    onOpenChange(false);
    setHasUpdateError(false);
    setHasAutoAppliedPromoCodes(false);
    setAlreadyAppliedPromoCodeError(false);
  };

  const isLoading = loading || queryLoading;
  const hasAutoAppliedPromoCodesAndDiscountErrors = !isLoading && alreadyAppliedPromoCodeError;
  const hasError = !isLoading && (hasUpdateError || hasAutoAppliedPromoCodesAndDiscountErrors);
  const isReadyToUpdate = !isLoading && !hasError;

  return (
    <DialogRoot open={open} onOpenChange={onOpenChange}>
      <Modal layout="drawer">
        <div className={css.root}>
          <div className={isReadyToUpdate ? css.introWithBorder : css.intro}>
            <div className={css.header}>
              <Text variant="bodyXLarge" color="burgundy" className={css.text}>
                {!loading ? 'Update Plan' : 'Updating Plan'}
              </Text>
              <Button icon="X" as={ModalClose} variant="secondary" />
            </div>
            {isReadyToUpdate && (
              <Text
                as="div"
                variant="body"
                color="doveGray"
                style={text.body.medium as unknown as React.CSSProperties}>
                <p>
                  Please confirm if you would like to update your subscription to include the
                  following items:
                </p>
              </Text>
            )}
            {hasError && (
              <div className={css.errorMessageWrapper}>
                <Text as="div" variant="body" color="doveGray">
                  {(pendingChanges?.addDiscountCodes?.length ?? 0) > 0 ? (
                    <Text as="div" variant="body" color="doveGray">
                      Sorry, we cannot apply this promo to your existing plan as it does not meet
                      the promotion criteria.
                      <br />
                      <br />
                      If you think that this is a mistake, please reach out to us at{' '}
                      <a className={css.mailPrivacy} href={`mailto:${csEmail}`}>
                        {csEmail + ' '}
                      </a>
                    </Text>
                  ) : (
                    <Text as="div" variant="body" color="doveGray">
                      Sorry, we encountered an issue building your new plan to confirm your updates.
                      Please return to Manage My Plan and try to re-apply your desired updates to
                      your plan.
                      <br />
                      <br />
                      If you continue to experience issues, please reach out to us at{' '}
                      <a className={css.mailPrivacy} href={`mailto:${csEmail}`}>
                        {csEmail + ' '}
                      </a>
                    </Text>
                  )}
                </Text>
                <Button onClick={handleReturnToManagePlan}>Return to Manage My Plan</Button>
              </div>
            )}
          </div>
          {loading || queryLoading ? (
            <div className={css.loadingStyle}>
              <UncertainProgress width={144} size={24} duration={1} />
            </div>
          ) : (
            isReadyToUpdate && (
              <>
                <div className={css.products}>
                  {updatedProducts.map((prod, _) => (
                    <PricedProductVariantRow
                      key={prod.id}
                      image={prod.imageURL}
                      title={prod.title}
                      value={prod.price}
                      discount={prod.discount}
                      plusSupplement={prod.plusSupplement}
                      display={prod.display}
                    />
                  ))}
                </div>
                <div className={css.priceTotal}>
                  <div className={css.totalWrapper}>
                    <div className={css.total}>
                      <div className={css.titleWrapper}>
                        <Text
                          as="div"
                          variant="heading3"
                          color="doveGray"
                          style={text.body.medium as unknown as React.CSSProperties}>
                          Subtotal
                        </Text>
                      </div>
                      <Text as="div" variant="body" color="doveGray" className={css.priceStyle}>
                        ${totalPriceWithoutDiscounts.toFixed(2)}
                      </Text>
                    </div>
                    {discounts.length > 0 && <DiscountSection discounts={discounts} />}
                    {discountErrors.length > 0 &&
                      discountErrors.map(error => (
                        <DiscountErrorMessageBox
                          key={error.discount}
                          discountCode={error.discount}
                        />
                      ))}
                  </div>
                </div>
                <div className={css.nextBilling}>
                  <div className={css.total}>
                    <div className={css.titleWrapper}>
                      <Text
                        as="div"
                        variant="button"
                        color="doveGray"
                        style={text.body.medium as unknown as React.CSSProperties}>
                        To be billed{' '}
                        {nextBillingDateAt
                          ? formattedDate(new Date(nextBillingDateAt))
                          : 'on next billing date'}
                      </Text>
                    </div>

                    <Text as="div" variant="body" color="doveGray" className={css.priceStyle}>
                      ${grandTotal.toFixed(2)}
                    </Text>
                  </div>
                  <div>
                    <Text as="div" variant="button" color="doveGray" className={css.taxesStyle}>
                      + applicable state/local taxes
                    </Text>
                  </div>
                </div>
                <Button onClick={handleConfirm} disabled={loading}>
                  {loading ? 'Confirming...' : 'Confirm'}
                </Button>
                {subscriptionStatus === SubscriptionStatusEnum.PAUSED && (
                  <div>
                    <Text
                      as="div"
                      variant="button"
                      color="doveGray"
                      className={css.pausedSubscriptionNoteStyle}>
                      Please note: Confirming this change will reactivate your subscription
                    </Text>
                  </div>
                )}
              </>
            )
          )}
        </div>
      </Modal>
    </DialogRoot>
  );
}
